import React, { Component } from 'react'

import {
    Grid,
    GridCell,
    Theme,
    Spinner,
    Icon,
} from "@bdfx/mergado-ui-kit"

import ShapeType, { IMAGE_FROM_ELEMENTS_TYPE } from "shape-entities/types/shape";

import {
    CustomImageSetFile,
    FormNameType,
} from "app/types/model-types";

import {
    getAllCustomImageSetFiles,
    deleteCustomImageSetFile,
    projectId,
    saveCustomImageFile as apiSaveCustomImageFile,
    godProjectId,
    isGodModeOn,
    customImageSetFileGetImageBlob,
} from "app/helpers/api";

import trans from "trans";
import { css } from "@bdfx/mergado-ui-kit";
import { getTemplateSize, reorderShapes } from "./helpers";
import store from "store";
import { createToast } from "actions";
import toastFactory from "app/helpers/toast_factory";
import {
    POST_CUSTOM_IMAGE_SET_FILE,
    DELETE_IMAGE_FROM_CUSTOM_SET,
} from "template-edit/reducers";
import { URL } from "app/config/api-config";
import ChangeSvgColors from "./change-svg-colors";
import CustomSetToolbar from "./custom-set-toolbar";
import CustomSetSourceInfo, { getSourceInfoTitle } from "./custom-set-source-info";
import { AxiosResponse } from "axios";
import uniqueId from "app/helpers/unique_id";
import { StyledToggler } from './common-shape-form';
import { intboolean } from 'app/types/app-types';
import ReflectionForm from './reflection-form';
import ShadowForm from './shadow-form';
import { iconDisabledColor } from 'template-edit/components/tools-panel';

const { round, max, min } = Math

interface Props {
    formName: FormNameType
    shape: ShapeType
    shapes: ShapeType[]
    change: Function
    activeShapeId: string
    setActiveShapeId: Function
}

interface State {
    allCustomSetFiles: CustomImageSetFile[]
    isLoading: boolean
}

const EMPTY_IMAGE_SHAPE = {
    imageBlob: '',
    custom_set_file_id: null,
    custom_set_id: null,
    width: 150,
    height: 150,
}

class CustomSetForm extends Component<Props, State> {
    uploadBatchShapes: any

    constructor(props) {
        super(props)

        this.state = {
            allCustomSetFiles: null,
            isLoading: false,
        }
    }

    componentDidMount() {
        this.getAllCustomSetFiles()
    }

    getAllCustomSetFiles = () => {
        this.setState({ isLoading: true })
        getAllCustomImageSetFiles()
            .then((response: any) => {
                this.setState({ allCustomSetFiles: response.data.reverse() })
            })
            .catch(error => {
                console.debug('error while loading custom image sets', error)
            })
            .finally(() => this.setState({ isLoading: false }))
    }

    handleRemoveImageFromSet = (removingSetFile: CustomImageSetFile) => {
        deleteCustomImageSetFile(removingSetFile)
            .then((response: AxiosResponse<any>) => {
                store.dispatch(createToast(toastFactory(DELETE_IMAGE_FROM_CUSTOM_SET.SUCCESS, response.data)))
            })
            .catch(error => {
                store.dispatch(createToast(toastFactory(DELETE_IMAGE_FROM_CUSTOM_SET.ERROR, error)))
            })
            .finally(() => {
                this.getAllCustomSetFiles()
            })
    }

    downloadImage = (selectedFile: CustomImageSetFile) => {
        const previewUrl = this.getImageUrl(selectedFile)
        const link = document.createElement('a')
        link.href = previewUrl
        link.download = selectedFile.filename
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
    }

    handleSelectSetFile = (selectedFile: CustomImageSetFile) => {
        const { shape, shapes } = this.props

        if (shape && shape.index) {
            const previewUrl = this.getImageUrl(selectedFile)

            let newWidth = selectedFile.width || 150
            let newHeight = selectedFile.height || 150

            const templateSize = getTemplateSize()
            if (templateSize && (newWidth > templateSize.width || newHeight > templateSize.height)) {
                const ratio = min(templateSize.width / newWidth, templateSize.height / newHeight)
                newWidth = round(newWidth * ratio)
                newHeight = round(newHeight * ratio)
            }

            const updatedShape = {
                ...shape,
                index: uniqueId(),
                priority: shape.priority + 1,
                imageBlob: selectedFile.base ? selectedFile.base : previewUrl,  //this should be in base64 - otherwise the screen snaps for previews and template.png will not work!
                width: newWidth,
                height: newHeight,
                custom_set_file_id: selectedFile.id,
                custom_set_id: selectedFile.set_id,
                name: selectedFile.orig_filename,
                is_remove_background: null,
            }

            //reorder shapes and set priority here:
            let newShapes = [];
            newShapes[updatedShape.index] = updatedShape
            reorderShapes({ ...newShapes, ...shapes }, [updatedShape.index, ...Object.keys(shapes)])
            this.props.setActiveShapeId(updatedShape.index)
        }
    }

    clearImage = () => {
        const { shape, formName } = this.props

        const updatedShape = {
            ...shape,
            ...EMPTY_IMAGE_SHAPE,
        }

        this.props.change(formName, `shapes.${shape.index}`, updatedShape)
    }

    getImageUrl = (setFile: CustomImageSetFile) => {
        const actualProjectId = isGodModeOn ? godProjectId : projectId
        return `${URL.laravelBase}customsets/${actualProjectId}/${setFile.set_id}/${setFile.filename}`;
    }

    handleIsRemoveBackground = (newValue: intboolean) => {
        const { shape, formName } = this.props

        customImageSetFileGetImageBlob(shape.custom_set_file_id, newValue, shape.index)
            .then((response: AxiosResponse<string>) => {
                const imageBlob = response.data

                this.props.change(formName, `shapes.${shape.index}`, {
                    ...shape,
                    imageBlob: imageBlob,
                    is_remove_background: newValue,
                })
            })
            .catch((error) => {
                console.error('customImageSetFileGetImageBlob error', error)
            })
    }

    render() {
        const { shape, formName, activeShapeId } = this.props
        const { allCustomSetFiles, isLoading } = this.state

        if (isLoading) {
            return <Spinner />
        }

        const isFileSelected = shape.custom_set_file_id && shape.custom_set_file_id
        const selectedSetFile = isFileSelected && allCustomSetFiles ? allCustomSetFiles.find(setFile => setFile.id === shape.custom_set_file_id) : null

        return (
            <div>
                {shape.custom_set_id && shape.imageBlob &&
                    <StyledButtonsDiv>
                        <StyledImg src={shape.imageBlob} title={trans('custom_image_set_selected_image')} />

                        {selectedSetFile &&
                            <CustomSetSourceInfo
                                key={selectedSetFile.id}
                                setFile={selectedSetFile}
                                onUpdated={(updatedSetFile) => this.setState({
                                    allCustomSetFiles: allCustomSetFiles.map(file => file.id === updatedSetFile.id ? updatedSetFile : file),
                                })}
                            />
                        }

                        <div>
                            <ChangeSvgColors shape={shape} />
                        </div>
                    </StyledButtonsDiv>
                }

                {isFileSelected &&
                    <React.Fragment>
                        <Grid cols={'auto max-content'} gap={'20px'}>
                            <GridCell
                                onClick={() => this.handleIsRemoveBackground(shape.is_remove_background ? 0 : 1)}
                                style={{ alignSelf: 'center', cursor: 'pointer' }}>
                                {trans('remove_background')}
                            </GridCell>
                            <GridCell style={{ alignSelf: 'center' }}>
                                <StyledToggler
                                    value={shape.is_remove_background}
                                    onChange={(newValue) => this.handleIsRemoveBackground(newValue)}
                                />
                            </GridCell>
                        </Grid>


                        <ReflectionForm
                            shape={shape}
                            formName={formName}
                            change={this.props.change}
                            activeShapeId={activeShapeId}
                        />
                        <ShadowForm
                            formName={formName}
                            shape={shape}
                            change={this.props.change}
                            activeShapeId={activeShapeId}
                        />
                    </React.Fragment>
                }

                <CustomSetToolbar
                    allCustomSetFiles={allCustomSetFiles}
                    onBatchStart={() => {
                        this.uploadBatchShapes = { ...this.props.shapes }
                    }}
                    onFileReady={(file, img, isLast) => {
                        const newIndex = isLast ? shape.index : uniqueId()

                        let newWidth = img.width
                        let newHeight = img.height

                        const templateSize = getTemplateSize()
                        if (templateSize && (newWidth > templateSize.width || newHeight > templateSize.height)) {
                            const ratio = min(templateSize.width / newWidth, templateSize.height / newHeight)
                            newWidth = round(newWidth * ratio)
                            newHeight = round(newHeight * ratio)
                        }

                        const newShape: ShapeType = {
                            ...shape,
                            index: newIndex,
                            width: newWidth,
                            height: newHeight,
                            x: round(.5*templateSize.width),
                            y: round(.5*templateSize.height),
                            scale: 1,
                            imageBlob: file.base64,
                            name: file.name,
                            custom_set_id: null,
                        }

                        apiSaveCustomImageFile(newShape, file.name)
                            .then((response: AxiosResponse<ShapeType>) => {
                                this.uploadBatchShapes[newIndex] = { ...response.data, index: newIndex }
                                this.props.change(formName, "shapes", this.uploadBatchShapes)
                                store.dispatch(createToast(toastFactory(POST_CUSTOM_IMAGE_SET_FILE.SUCCESS)))
                            })
                            .catch(error => {
                                console.error('cannot upload new file', error)
                                store.dispatch(createToast(toastFactory(POST_CUSTOM_IMAGE_SET_FILE.ERROR, error)))
                            })
                            .finally(() => {
                                this.getAllCustomSetFiles()
                            })
                    }}
                />

                <Grid cols="repeat(auto-fit, minmax(150px, 1fr))" gap={"5px"} className={'images-from-set'} style={{ marginTop: '20px' }}>
                    {allCustomSetFiles && allCustomSetFiles.map((setFile: CustomImageSetFile, i: number) => {
                        const previewUrl = this.getImageUrl(setFile)
                        return (
                            <CustomSetItem key={`set_image_file_${setFile.id}`}>
                                {setFile.is_used === null &&
                                    <span className='remove' title={trans('custom_image_set_remove_image')} onClick={() => this.handleRemoveImageFromSet(setFile)}>
                                        <Icon type={'trash'} size={20} color={Theme.red} />
                                    </span>
                                }

                                <span className='download' title={trans('download_image')} onClick={() => this.downloadImage(setFile)}>
                                    <Icon type={'download-radix'} size={20} color={Theme.blue} />
                                </span>

                                <img
                                    src={previewUrl}
                                    title={getSourceInfoTitle(setFile)}
                                    className={shape && shape.custom_set_file_id && shape.custom_set_file_id === setFile.id ? 'active' : ''}
                                    onClick={() => this.handleSelectSetFile(setFile)}
                                />
                            </CustomSetItem>
                        )
                    })}
                </Grid>
            </div>
        )
    }
}

const StyledImg = css('img')({
    textAlign: 'center',
    maxWidth: '80%',
    maxHeight: '200px',
    width: 'auto',
    height: 'auto',
});

export const CustomSetItem = css(GridCell)({
    position: 'relative',
    backgroundColor: "#eee",
    borderRadius: "5px",
    textAlign: "center",
    padding: "10px",
    paddingBottom: "5px",
    cursor: "pointer",
    fontSize: "14px",
    color: "#333",
    alignSelf: 'center',
    transition: "background-color 0.2s",
    ":hover": {
        backgroundColor: "#ddd",
    },
    " path": {
        fill: "#333"
    },
    " .muk-button": {
        fontWeight: "normal",
        color: "#333",
    },
    ' .remove': {
        top: '10px',
        right: '10px',
        position: 'absolute',
        width: '32px',
        height: '32px',
        background: '#ffffffa1',
        borderRadius: '50%',
        padding: '3px',
    },
    ' .download': {
        top: '10px',
        left: '10px',
        position: 'absolute',
        width: '32px',
        height: '32px',
        background: '#ffffffa1',
        borderRadius: '50%',
        padding: '3px',
    },
    ' img': {
        maxWidth: '100%',
        maxHeight: '100px',
    },
    ' .small': {
        fontSize: 'small',
        color: iconDisabledColor,
    }
})

const StyledButtonsDiv = css('div')({
    textAlign: 'center',
    alignSelf: 'center',
})

export default CustomSetForm